import styled from '@emotion/styled';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import { format } from 'date-fns';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { HardSoftSkill, Skill, SkillWithIconColor } from '~/shared/data-contract/skill';
import { hardSoftSkillProficiencyLevelSchema, languageSkillProficiencyLevelSchema } from '~/shared/data-contract/skill';

import { isLanguageSkill } from '../../app/common/skills';
import { SkillLabel } from '../../components/SkillLabel';
import { HardSkillIcon } from './assets/HardSkillIcon';
import { LanguageSkillIcon } from './assets/LanguageSkillIcon';
import { SoftSkillIcon } from './assets/SoftSkillIcon';
import { HeadlineMedium } from './components/HeadlineMedium';
import { PrimaryButton } from './components/PrimaryButton';
import { SecondaryText } from './components/SecondaryText';
import { SkillMosaicWrapper } from './components/SkillMosaicWrapper';
import { StepHeadline } from './components/StepHeadline';
import { getSkillIcon } from './helpers/get-skill-icon';
import { SkillMosaic } from './SkillMosaic';
import { useCompanyOnboardingStore } from './use-company-onboarding-store';

type SoftSkillsAmountProps = {
    readonly amount: number;
};

const SkillMosaicAmountWrapper = styled.div({
    alignItems: 'center',
    display: 'flex',
    gap: '0.5rem',
});

const IconWrapper = styled.div({
    display: 'flex',
    height: '12px',
    width: '12px',
});

const SoftSkillsAmount: FunctionComponent<SoftSkillsAmountProps> = ({ amount }) => {
    return (
        <SkillMosaicAmountWrapper>
            <IconWrapper>
                <SoftSkillIcon />
            </IconWrapper>
            <div>{amount} Soft Skills</div>
        </SkillMosaicAmountWrapper>
    );
};

type HardSkillsAmountProps = {
    readonly amount: number;
};

const HardSkillsAmount: FunctionComponent<HardSkillsAmountProps> = ({ amount }) => {
    return (
        <SkillMosaicAmountWrapper>
            <IconWrapper>
                <HardSkillIcon />
            </IconWrapper>
            <div>{amount} Hard Skills</div>
        </SkillMosaicAmountWrapper>
    );
};

const LanguageSkillsAmount: FunctionComponent = () => {
    const employeeLanguageSkills = useCompanyOnboardingStore((state) => state.employeeLanguageSkills);

    if (employeeLanguageSkills.isNothing) {
        return null;
    }

    return (
        <SkillMosaicAmountWrapper>
            <IconWrapper>
                <LanguageSkillIcon />
            </IconWrapper>
            <div>{employeeLanguageSkills.value.length} Language Skills</div>
        </SkillMosaicAmountWrapper>
    );
};

function formatDatetime(): string {
    const date = new Date();
    const formattedDate = format(date, 'MMMM. dd, yyyy');

    return formattedDate;
}

const MosaicStyledWrapper = styled.div({
    background: '#f6f6f7',
    borderRadius: '1rem',
    boxShadow:
        '0.8px 1px 1px 0px rgba(251, 251, 255, 0.7) inset, -1px -0.8px 1px 0px rgba(251, 251, 255, 0.5) inset, 0px 17px 7px 0px rgba(80, 80, 82, 0.01), 0px 9px 6px 0px rgba(80, 80, 82, 0.05), 0px 4px 4px 0px rgba(80, 80, 82, 0.09), 0px 1px 2px 0px rgba(80, 80, 82, 0.1)',
    padding: '2rem',
});

const UserInformationWrapper = styled.div({
    marginTop: '3.5rem',
});

const UserInformation: FunctionComponent = () => {
    const { applicationState } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;

    return (
        <UserInformationWrapper>
            <HeadlineMedium>{`${myUserProfile.get('firstName').unwrapOr('')} ${myUserProfile.get('lastName').unwrapOr('')} `}</HeadlineMedium>
            <SecondaryText>{formatDatetime()}</SecondaryText>
        </UserInformationWrapper>
    );
};

const MosaicSkillsWrapper = styled.div({
    marginTop: '2.8125rem',
});

const MosaicSkills: FunctionComponent = () => {
    const employeeHardSoftSkills = useCompanyOnboardingStore((state) => state.employeeHardSoftSkills);

    if (employeeHardSoftSkills.isNothing) {
        return null;
    }

    const hardSkills = employeeHardSoftSkills.value.filter((skill) => skill.skillType === 'hard');
    const softSkills = employeeHardSoftSkills.value.filter((skill) => skill.skillType === 'soft');

    return (
        <MosaicSkillsWrapper>
            <SoftSkillsAmount amount={softSkills.length} />
            <HardSkillsAmount amount={hardSkills.length} />
            <LanguageSkillsAmount />
        </MosaicSkillsWrapper>
    );
};

const UserSkillsWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1.5rem',
});

type OptionType = {
    readonly label: string;
    readonly value: number;
};

type ProficiencyDropdownProps = {
    readonly onChange: (event: number) => void;
    readonly options: readonly OptionType[];
    readonly value: string;
};

const ProficiencyDropdown: FunctionComponent<ProficiencyDropdownProps> = ({ onChange, options, value }) => {
    return (
        <Select
            disableUnderline={true}
            displayEmpty={true}
            renderValue={() => value}
            value={value}
            variant="standard"
            onChange={(event) => {
                const value = Number(event.target.value);
                onChange(value);
            }}
        >
            {options.map((item) => {
                return (
                    <MenuItem key={item.value} value={item.value}>
                        <Typography>{item.label}</Typography>
                    </MenuItem>
                );
            })}
        </Select>
    );
};

type DropdownProps = {
    readonly level: number;
    readonly onChange: (value: number) => void;
};

export const LanguageDropdown: FunctionComponent<DropdownProps> = ({ level, onChange }) => {
    const { translate } = useContext(ApplicationContext);
    const options = [...languageSkillProficiencyLevelSchema.options]
        .sort((a, b) => b.value - a.value)
        .map((option) => ({
            label: translate(`dialogs.skills.proficiency.language.${option.value}`),
            value: option.value,
        }));

    const currentProficiency = options.find((option) => option.value === level);

    return <ProficiencyDropdown options={options} value={currentProficiency?.label ?? ''} onChange={onChange} />;
};

export const HardSoftSkillDropdown: FunctionComponent<DropdownProps> = ({ level, onChange }) => {
    const { translate } = useContext(ApplicationContext);
    const options = [...hardSoftSkillProficiencyLevelSchema.options]
        .sort((a, b) => b.value - a.value)
        .map((option) => ({
            label: translate(`dialogs.skills.proficiency.skills.${option.value}`),
            value: option.value,
        }));

    const currentProficiency = options.find((option) => option.value === level);

    return <ProficiencyDropdown options={options} value={currentProficiency?.label ?? ''} onChange={onChange} />;
};

const UserSkillWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

const ProficiencyGroupWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const ProficiencyDropdownWrapper = styled.div({
    width: 'auto',
});

const groupSkillsByProficiency = (
    skills: readonly SkillWithIconColor[],
): readonly (readonly [string, readonly SkillWithIconColor[]])[] => {
    const skillRecord = skills.reduce(
        (acc, skill) => {
            const levelKey = skill.level ?? 0;

            return {
                // biome-ignore lint/performance/noAccumulatingSpread: suppressed due to migration
                ...acc,
                [levelKey]: [...(acc[levelKey] ?? []), skill],
            };
        },
        {} as Record<number, readonly SkillWithIconColor[]>,
    );

    return Object.entries(skillRecord).sort(([levelA], [levelB]) =>
        levelB.localeCompare(levelA, 'en-US', { numeric: true }),
    );
};

const SkillIconWrapper = styled.div({
    display: 'flex',
    height: '12px',
    width: '12px',
});

export const isHardSoftSkill = (skill: Pick<Skill, 'skillType'>): skill is HardSoftSkill =>
    ['hard', 'soft'].includes(skill.skillType);

const UserSkills: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const employeeHardSoftSkills = useCompanyOnboardingStore((state) => state.employeeHardSoftSkills);
    const employeeLanguageSkills = useCompanyOnboardingStore((state) => state.employeeLanguageSkills);
    const groupedHardSoftSkills = employeeHardSoftSkills.map((skills) => groupSkillsByProficiency(skills));
    const groupedLanguageSkills = employeeLanguageSkills.map((skills) => groupSkillsByProficiency(skills));
    const changeEmployeeSkillProficiency = useCompanyOnboardingStore((state) => state.changeEmployeeSkillProficiency);

    const getProficiencyLevelName = (skill: Skill): string => {
        const { level } = skill;
        const isSkillLevelUnknown = level === 0;

        if (is.null(level) || isSkillLevelUnknown) {
            return '';
        }

        if (isLanguageSkill(skill) && !is.nullOrUndefined(skill.level)) {
            return translate(`dialogs.skills.proficiency.language.${skill.level}`);
        }

        if (isHardSoftSkill(skill) && !is.nullOrUndefined(skill.level)) {
            return translate(`dialogs.skills.proficiency.skills.${skill.level}`);
        }

        return '';
    };

    const skills = [...groupedHardSoftSkills.unwrapOr([]), ...groupedLanguageSkills.unwrapOr([])];

    return (
        <ProficiencyGroupWrapper>
            {skills.map(([level, skillList]) => {
                const skill = skillList.at(0)!;

                return (
                    <UserSkillsWrapper key={`${skill.skillType}-${level}`}>
                        <HeadlineMedium>{getProficiencyLevelName(skill)}</HeadlineMedium>
                        {skillList.map((skill) => {
                            const onChange = (skillLevel: number): void => {
                                changeEmployeeSkillProficiency(skill.id, skillLevel);
                            };

                            return (
                                <UserSkillWrapper key={skill.title}>
                                    <SkillLabel>
                                        <SkillIconWrapper>{getSkillIcon(skill)}</SkillIconWrapper>
                                        <div>{skill.title}</div>
                                    </SkillLabel>
                                    <ProficiencyDropdownWrapper>
                                        {skill.skillType === 'language' ? (
                                            <LanguageDropdown level={skill.level ?? 0} onChange={onChange} />
                                        ) : (
                                            <HardSoftSkillDropdown level={skill.level ?? 0} onChange={onChange} />
                                        )}
                                    </ProficiencyDropdownWrapper>
                                </UserSkillWrapper>
                            );
                        })}
                    </UserSkillsWrapper>
                );
            })}
        </ProficiencyGroupWrapper>
    );
};
const SkillsStepWrapper = styled.div({
    display: 'flex',
    gap: '6.5rem',
    width: 'min-content',
});

const FloatingActionWrapper = styled.div({
    bottom: '0',
    display: 'flex',
    justifyContent: 'center',
    position: 'sticky',
    width: '100%',
    zIndex: '10',
});

const FloatingActionContainer = styled.div({
    alignItems: 'center',
    backgroundColor: '#fbfbff',
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    padding: '1rem',
    paddingRight: '0',
    width: '100%',
});

const MainContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    marginTop: '2.5rem',
    width: 'min-content',
});

export const ReviewSkillsStep: FunctionComponent = () => {
    const finishOnboardingLabel = useCompanyOnboardingStore((state) => state.finishOnboardingLabel);
    const finishOnboarding = useCompanyOnboardingStore((state) => state.finishOnboarding);

    return (
        <div>
            <StepHeadline>Please review your skills</StepHeadline>
            <MainContainer>
                <SkillsStepWrapper>
                    <SkillMosaicWrapper>
                        <MosaicStyledWrapper>
                            <SkillMosaic />
                            <UserInformation />
                            <MosaicSkills />
                        </MosaicStyledWrapper>
                    </SkillMosaicWrapper>
                    <UserSkills />
                </SkillsStepWrapper>
                <FloatingActionWrapper>
                    <FloatingActionContainer>
                        <PrimaryButton onClick={finishOnboarding}>{finishOnboardingLabel}</PrimaryButton>
                    </FloatingActionContainer>
                </FloatingActionWrapper>
            </MainContainer>
        </div>
    );
};
